import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Slider,
  Button,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
  Drawer,
  IconButton,
  Fab,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  FormControlLabel,
  Switch,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import BubbleChartIcon from '@mui/icons-material/BubbleChart';
import PlaceIcon from '@mui/icons-material/Place';
import PotholeMap from '../components/Map/PotholeMap';
import { potholesApi, usersApi } from '../api/client';
import { useLanguage } from '../i18n';

type ViewMode = 'heatmap' | 'points';

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useLanguage();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);

  // View mode
  const [viewMode, setViewMode] = useState<ViewMode>('heatmap');

  // Potholes data and filters
  const [features, setFeatures] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hours, setHours] = useState<number>(24); // Default 24 hours for heatmap

  // Map settings (loaded from user preferences)
  const [mapCenter, setMapCenter] = useState<[number, number]>([56.9496, 24.1052]);
  const [mapZoom, setMapZoom] = useState(12);

  // Auto-refresh settings
  const AUTO_REFRESH_INTERVAL = 60000; // 60 seconds
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [, setTick] = useState(0); // For forcing re-render of time display
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Load user preferences on mount
  useEffect(() => {
    const loadPreferences = async () => {
      try {
        const response = await usersApi.getPreferences();
        const prefs = response.data;

        if (prefs.default_hours) {
          setHours(prefs.default_hours);
        }
        if (prefs.map_center_lat && prefs.map_center_lng) {
          setMapCenter([
            parseFloat(prefs.map_center_lat),
            parseFloat(prefs.map_center_lng),
          ]);
        }
        if (prefs.map_zoom) {
          setMapZoom(prefs.map_zoom);
        }
      } catch (err) {
        // Use defaults if preferences can't be loaded
      } finally {
        setPreferencesLoaded(true);
      }
    };
    loadPreferences();
  }, []);

  // Fetch potholes - uses different endpoint based on view mode
  const fetchPotholes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let response;
      if (viewMode === 'heatmap') {
        // Heatmap/clusters: use heatmap endpoint (includes historical data)
        response = await potholesApi.getHeatmapPotholes(hours);
      } else {
        // Points: use all endpoint (currently online potholes only)
        response = await potholesApi.getAllPotholes(hours || undefined);
      }
      setFeatures(response.data.features || []);
      setTotalCount(response.data.total_count || response.data.features?.length || 0);
      setLastUpdated(new Date());
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch pothole data');
    } finally {
      setLoading(false);
    }
  }, [hours, viewMode]);

  // Fetch data when filters change (wait for preferences to load first)
  useEffect(() => {
    if (!preferencesLoaded) return;
    const timer = setTimeout(() => {
      fetchPotholes();
    }, 500);
    return () => clearTimeout(timer);
  }, [fetchPotholes, preferencesLoaded]);

  // Auto-refresh interval
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        fetchPotholes();
      }, AUTO_REFRESH_INTERVAL);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [autoRefresh, fetchPotholes]);

  // Update time display every 30 seconds
  useEffect(() => {
    const tickInterval = setInterval(() => {
      setTick(t => t + 1);
    }, 30000);
    return () => clearInterval(tickInterval);
  }, []);

  const handleRefresh = () => {
    fetchPotholes();
  };

  // Format last updated time
  const formatLastUpdated = (date: Date | null): string => {
    if (!date) return '';
    const now = new Date();
    const diffSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (diffSeconds < 60) return t('dashboard.justNow');
    const diffMinutes = Math.floor(diffSeconds / 60);
    if (diffMinutes === 1) return t('dashboard.minuteAgo');
    return t('dashboard.minutesAgo', { minutes: diffMinutes });
  };

  const handleViewModeChange = (
    _: React.MouseEvent<HTMLElement>,
    newMode: ViewMode | null
  ) => {
    if (newMode !== null) {
      setViewMode(newMode);
      // When switching to heatmap, ensure hours is at least 1
      if (newMode === 'heatmap' && hours === 0) {
        setHours(24);
      }
    }
  };

  const filterContent = (
    <Box sx={{ p: 2, width: isMobile ? '100%' : 280 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="h6">{t('dashboard.viewAndFilters')}</Typography>
        {isMobile && (
          <IconButton onClick={() => setFilterDrawerOpen(false)}>
            <CloseIcon />
          </IconButton>
        )}
      </Box>

      {/* View Mode Toggle */}
      <Typography variant="subtitle2" gutterBottom>
        {t('dashboard.viewMode')}
      </Typography>
      <ToggleButtonGroup
        value={viewMode}
        exclusive
        onChange={handleViewModeChange}
        fullWidth
        size="small"
        sx={{ mb: 2 }}
      >
        <ToggleButton value="heatmap">
          <BubbleChartIcon sx={{ mr: 0.5, fontSize: 18 }} />
          {t('dashboard.heatmap')}
        </ToggleButton>
        <ToggleButton value="points">
          <PlaceIcon sx={{ mr: 0.5, fontSize: 18 }} />
          {t('dashboard.points')}
        </ToggleButton>
      </ToggleButtonGroup>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle2" gutterBottom>
        {t('dashboard.timeFilter')}
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {viewMode === 'points' && hours === 0
          ? t('dashboard.allTime')
          : t('dashboard.lastHours', { hours })}
      </Typography>
      <Slider
        value={hours}
        onChange={(_, value) => {
          const newHours = value as number;
          // For heatmap mode, minimum is 1 hour (API requires a time period)
          if (viewMode === 'heatmap' && newHours === 0) {
            setHours(1);
          } else {
            setHours(newHours);
          }
        }}
        min={viewMode === 'heatmap' ? 1 : 0}
        max={168}
        marks={
          viewMode === 'heatmap'
            ? [
                { value: 1, label: `1${t('time.hours')}` },
                { value: 24, label: `24${t('time.hours')}` },
                { value: 72, label: `3${t('time.days')}` },
                { value: 168, label: `7${t('time.days')}` },
              ]
            : [
                { value: 0, label: t('time.all') },
                { value: 24, label: `24${t('time.hours')}` },
                { value: 72, label: `3${t('time.days')}` },
                { value: 168, label: `7${t('time.days')}` },
              ]
        }
        sx={{ mb: 3 }}
      />

      <Button
        variant="outlined"
        fullWidth
        startIcon={<RefreshIcon />}
        onClick={handleRefresh}
        disabled={loading}
        sx={{ mb: 1 }}
      >
        {t('dashboard.refresh')}
      </Button>

      <FormControlLabel
        control={
          <Switch
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
            size="small"
          />
        }
        label={t('dashboard.autoRefresh')}
        sx={{ mb: 1 }}
      />

      {lastUpdated && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          {t('dashboard.lastUpdated')}: {formatLastUpdated(lastUpdated)}
        </Typography>
      )}

      {/* Stats */}
      <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {t('dashboard.showing')} <strong>{features.length}</strong> {t('dashboard.potholes')}
        </Typography>
        {totalCount > features.length && (
          <Typography variant="caption" color="text.secondary">
            {t('dashboard.total')}: {totalCount}
          </Typography>
        )}
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
          {viewMode === 'heatmap'
            ? t('dashboard.heatmapHint')
            : t('dashboard.pointsHint')}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex' }}>
      {/* Desktop filter panel */}
      {!isMobile && (
        <Paper
          elevation={0}
          sx={{
            width: 280,
            borderRight: 1,
            borderColor: 'divider',
            overflow: 'auto',
          }}
        >
          {filterContent}
        </Paper>
      )}

      {/* Mobile filter drawer */}
      {isMobile && (
        <Drawer
          anchor="bottom"
          open={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
          PaperProps={{
            sx: {
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              maxHeight: '80vh',
            },
          }}
        >
          {filterContent}
        </Drawer>
      )}

      {/* Map area */}
      <Box sx={{ flex: 1, position: 'relative' }}>
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              top: 16,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1000,
              bgcolor: 'background.paper',
              px: 2,
              py: 1,
              borderRadius: 1,
              boxShadow: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <CircularProgress size={20} />
            <Typography variant="body2">Loading...</Typography>
          </Box>
        )}

        {error && (
          <Alert
            severity="error"
            sx={{
              position: 'absolute',
              top: 16,
              left: 16,
              right: 16,
              zIndex: 1000,
            }}
          >
            {error}
          </Alert>
        )}

        <PotholeMap
          features={features}
          center={mapCenter}
          zoom={mapZoom}
          mode={viewMode}
        />

        {/* Mobile filter FAB */}
        {isMobile && (
          <Fab
            color="primary"
            sx={{
              position: 'absolute',
              bottom: 16,
              right: 16,
              zIndex: 1000,
            }}
            onClick={() => setFilterDrawerOpen(true)}
          >
            <FilterListIcon />
          </Fab>
        )}
      </Box>
    </Box>
  );
};

export default Dashboard;
